import React, { useState } from "react";
import Switch from "@mui/material/Switch";
import { useHistory } from "react-router";
import { Box } from "../../UI/Box";
import { Grid } from "../../UI/Grid";
import { Typography } from "../../UI/Typography";
import { Link } from "../../UI/Link";
import { MainLayout } from "../../layout/MainLayout";
import { AdminNotificationsTable } from "../../components/AdminNotificationsTable";
import { AddAdminNotificationButton } from "../../components/AddAdminNotificationButton";
import { WeeklyReportSchedule } from "../../components/WeeklyReportSchedule";
import { useNetwork } from "../../hooks/useNetwork";
import { ReportSubscription } from "../../services/AdminApi";
import useAuth from "../login/AuthContext";
import Api from "../../services/Api";
const MAX_GROUPS_URL = "/settings/match-rules/max-groups";

export const AdminNotificationsPage = () => {
  const { push } = useHistory();
  const { selectedNetwork } = useNetwork();
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = React.useState<ReportSubscription[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const { getAuthToken, isWriteAccess } = useAuth();

  async function loadSubscriptions(l1Id: number, withLoading = true) {
    setIsLoading(withLoading);
    const response = await Api.loadReportSubscriptions(l1Id, await getAuthToken());

    if (!response.error) {
      setSubscriptions(response.result?.subscriptions ?? []);
    }
    setIsLoading(false);
  }

  React.useEffect(() => {
    const { l1Id } = selectedNetwork;

    if (l1Id) {
      loadSubscriptions(l1Id);
    }
  }, [selectedNetwork.l1Id]);

  const reloadSubscriptions = async () => {
    await loadSubscriptions(selectedNetwork.l1Id, false);
  };

  const handleToggleArchiveChange = (_: any, checked: boolean) => setShowArchived(checked);

  return (
    <MainLayout
      title="Activity Report"
      endAdornment={
        <Grid container>
          <Grid item>
            <Typography>
              Show Archived
              <Switch
                disabled={!selectedNetwork.l1Id}
                value={showArchived}
                onChange={handleToggleArchiveChange}
              />
            </Typography>
          </Grid>
          {isWriteAccess() && (
            <Grid item>
              <AddAdminNotificationButton reloadSubscriptions={reloadSubscriptions} />
            </Grid>
          )}
        </Grid>
      }
    >
      {isWriteAccess() && (
        <>
          <WeeklyReportSchedule l1Id={selectedNetwork.l1Id} />
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>The Activity Report</strong> sends automated email summaries to help you stay on top of your network&apos;s roommate matching activity.
          </Typography>
          <Box
            component="ul"
            sx={{
              mt: 0,
              mb: 2,
              pl: 4,
              listStyleType: "circle",
            }}
          >
            <Box component="li" sx={{ mb: 1 }}>
              <Typography variant="body2" component="span">
                <strong>Daily report:</strong> sent each day with the previous day&apos;s activity: new user registrations, new matches, and unmatches. Only sent when there is activity to report.
              </Typography>
            </Box>
            <Box component="li">
              <Typography variant="body2" component="span">
                <strong>Weekly report:</strong> sent once a week with a summary of the previous week: invited users, registered users, and matches created, broken down by Matching Round. Includes a warning if any room type has exceeded its{" "}
                <Link onClick={() => push(MAX_GROUPS_URL)}>maximum group capacity</Link>.
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reports are only sent for Matching Rounds that are currently in the matching status.
          </Typography>
          <AdminNotificationsTable
            isLoading={isLoading}
            subscriptions={subscriptions}
            reloadSubscriptions={reloadSubscriptions}
          />
        </>
      )}
    </MainLayout>
  );
};
